import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/* eslint-disable prefer-spread */

/* eslint-disable no-param-reassign */

/* eslint-disable no-nested-ternary */
import moment, { weekdays } from 'moment';
import { Headers } from 'node-fetch';
import { Doctor, Aide, Nurse, SocialWorker, Spiritual, Volunteer, VolunteerCoordinatorRG } from './roleData';
import { ATTENDING_PHYSICIAN, IDGDocument, PSOFacility, PSOHome, cppEditingAllowedRoleGroup, HAD, HUV1, HUV2, EAO, EAC, ElectionAddendumFormKeys, ElectionAddendumNames, INAForm, InProgress, InProgressDriving, NVRForm } from './constants';
import { formatDateMMDDYY, formatDateTimeMMDDpm, formatPhysicianName, formatPhysicianNameLastFirst } from './formatters';
import en from './translationDefaults/en';
export const isActiveVisitStatus = status => status === InProgress || status === InProgressDriving;
/**
 * EA owed when the server sends daysRemaining
 * (0 = due today, positive = due in N days, negative = overdue by |N| days).
 * null/undefined (eaDetails null) means not required.
 */

export const isElectionAddendumRequired = daysRemaining => daysRemaining != null;
/**
 * True if a visit attribute is selected.
 * Handles PatientVisit.visitAttributes (`isSelected`) and getvisits
 * visitattrsumdto JSON (`isselected`).
 */

export const isVisitAttributeSelected = visitAtt => (visitAtt === null || visitAtt === void 0 ? void 0 : visitAtt.isSelected) === true || (visitAtt === null || visitAtt === void 0 ? void 0 : visitAtt.isselected) === true;
/** True if visitAttributes has shortname with isSelected/isselected true. */

export const hasSelectedVisitAttribute = (visitAttributes, shortname) => isVisitAttributeSelected(visitAttributes === null || visitAttributes === void 0 ? void 0 : visitAttributes.find(attr => attr.shortname === shortname));
/** True when visit has EA Offered (EAO) selected. */

export const isElectionAddendumOffered = visit => hasSelectedVisitAttribute(visit === null || visit === void 0 ? void 0 : visit.visitAttributes, EAO);
/** True when visit has EA Created (EAC) selected. */

export const isElectionAddendumCreated = visit => hasSelectedVisitAttribute(visit === null || visit === void 0 ? void 0 : visit.visitAttributes, EAC);
/** EAO selected and EAC not — chip / persist-EAC path. */

export const isElectionAddendumCreationPending = visit => isElectionAddendumOffered(visit) && !isElectionAddendumCreated(visit);

const normalizeEaToken = value => (value !== null && value !== void 0 ? value : '').trim().toLowerCase();

const ElectionAddendumFormKeysLower = ElectionAddendumFormKeys.map(key => key.toLowerCase());
const ElectionAddendumNamesLower = ElectionAddendumNames.map(name => name.toLowerCase());
/**
 * True when formkey is the US or ES Election Addendum key (case-insensitive).
 * Also matches versioned Fajita keys (e.g. hz-v1-es-election-addendum-fajver-5).
 */

export const isElectionAddendumFormKey = formkey => {
  const key = normalizeEaToken(formkey);

  if (key === '') {
    return false;
  } // startsWith covers exact base keys and suffixes like -fajver-N


  return ElectionAddendumFormKeysLower.some(base => key.startsWith(base));
};
/** True when display name is US or ES Election Addendum (case-insensitive). */

export const isElectionAddendumFormName = name => {
  const normalized = normalizeEaToken(name);
  return normalized !== '' && ElectionAddendumNamesLower.includes(normalized);
};
/**
 * Identify Election Addendum (US or ES) by formkey first; fall back to
 * formname / form_name / name. All comparisons are case-insensitive.
 */

export const isElectionAddendumForm = form => isElectionAddendumFormKey(form.formkey) || isElectionAddendumFormName(form.formname) || isElectionAddendumFormName(form.form_name) || isElectionAddendumFormName(form.name);
/**
 * New visitAttributes array with shortname isSelected set.
 * Catalog (GET /web/api/patientvisits/visitattributes) is insurance only:
 * visits_v2 create already returns the full list including EAO/EAC — then we
 * only toggle isSelected. Catalog is used when the visit list is null/empty
 * or missing that shortname (some GET/hydrate/PUT payloads).
 */

export const withVisitAttributeSelected = (visitAttributes, shortname, isSelected, catalog) => {
  const attrs = (visitAttributes !== null && visitAttributes !== void 0 ? visitAttributes : []).map(attr => _objectSpread({}, attr));
  const existing = attrs.find(attr => attr.shortname === shortname);

  if (existing) {
    existing.isSelected = isSelected;
    return attrs;
  }

  const fromCatalog = catalog === null || catalog === void 0 ? void 0 : catalog.find(attr => attr.shortname === shortname);

  if (fromCatalog) {
    attrs.push(_objectSpread(_objectSpread({}, fromCatalog), {}, {
      isSelected
    }));
    return attrs;
  }

  attrs.push({
    id: null,
    shortname,
    name: null,
    selectable: false,
    uiavailable: false,
    isnonpatienttype: false,
    isSelected
  });
  return attrs;
};
/** EAC selected, EAO cleared (mutually exclusive created vs offered). */

export const withElectionAddendumCreated = (visitAttributes, catalog) => {
  const withEac = withVisitAttributeSelected(visitAttributes, EAC, true, catalog);
  return withVisitAttributeSelected(withEac, EAO, false, catalog);
};
/** EAO selected, EAC cleared (mutually exclusive offered vs created). */

export const withElectionAddendumOffered = (visitAttributes, catalog) => {
  const withEao = withVisitAttributeSelected(visitAttributes, EAO, true, catalog);
  return withVisitAttributeSelected(withEao, EAC, false, catalog);
};
/**
 * Visit PUTs must not send appVisit.
 *
 * Object | Source | Used for
 * appVisit | StartVisit subset, getvisits hydrate, loadInProgress merge
 *   ({ ...getvisits, ...pim }) | Titles, notes, form engine, EA chip
 * savedVisit / PIM GET | createPatientVisits, getVisitId / getPatientVisit,
 *   last PUT response | visits_v2 PUT
 *
 * PIM PatientVisitDTO uses Jackson FAIL_ON_UNKNOWN. Getvisits fields
 * (person_id, durationmins, visittype, …) are fine on appVisit and invalid
 * on PUT. StartVisit’s appVisit is also a sparse subset — picking write keys
 * from it omits required fields (careProviderNumber, startDate, duration,
 * visitType) → 400 Required fields are missing.
 *
 * How existing PUTs stay valid
 *
 * Dashboard (duration timer, attributes icon, HOPE save, document time,
 * close visit, HCPCS, EA offered): { ...savedVisit } or
 * { ...initSavedVisit, visitAttributes }.
 * Visit log and forms EA submit (only have appVisit / grid row):
 * pim.getPatientVisit(...) first, mutate that, PUT that.
 */

/**
 * Patient Assistance
 */

export const getUserFirstWeekDay = currentUser => {
  const firstDayOfWeek = currentUser.startOfWeek || weekdays()[0];
  return moment().day(firstDayOfWeek);
};
/**
 * Meds Widget
 */

/**
 * OP-33278: physician option names. `label` is what the pick-list shows
 * ("Last, First, Cred"); `fullName` is the natural "First Last, Cred" for
 * order / certification sentences.
 */

export const physicianOptionNames = (firstName, lastName, credentials) => ({
  label: formatPhysicianNameLastFirst(firstName, lastName, credentials),
  fullName: formatPhysicianName('', firstName, lastName, credentials || '')
});
export const mapDoctors = doctorArray => (doctorArray !== null && doctorArray !== void 0 ? doctorArray : []).map(doctor => _objectSpread(_objectSpread({}, physicianOptionNames(doctor.firstName, doctor.lastName, doctor.credentials || '')), {}, {
  value: doctor.id,
  npi: (doctor === null || doctor === void 0 ? void 0 : doctor.npi) || ''
})).filter(doctor => String(doctor.label).trim() !== '');
export const mapDoctorsOptions = doctorArray => doctorArray ? doctorArray.map(doctor => _objectSpread(_objectSpread({}, physicianOptionNames(doctor.firstName, doctor.lastName, doctor.credentials)), {}, {
  firstName: doctor.firstName,
  lastName: doctor.lastName,
  credentials: doctor.credentials || '',
  personNumber: doctor.personNumber,
  value: doctor.personNumber,
  id: doctor.id,
  npi: (doctor === null || doctor === void 0 ? void 0 : doctor.npi) || ''
})) // OP-33278: a row without any name is not selectable, drop it
.filter(doctor => doctor.label !== '') : [];
export const mapAdmitDoctorsOptions = doctorArray => doctorArray ? doctorArray.map(doctor => _objectSpread(_objectSpread({}, physicianOptionNames(doctor.firstName, doctor.lastName, doctor.credentials)), {}, {
  firstName: doctor.firstName,
  lastName: doctor.lastName,
  credentials: doctor.credentials || '',
  personNumber: doctor.personNumber,
  value: doctor.personNumber,
  id: doctor.id,
  npi: (doctor === null || doctor === void 0 ? void 0 : doctor.npi) || '',
  pecoshospiceflag: (doctor === null || doctor === void 0 ? void 0 : doctor.pecoshospiceflag) === 'Y'
})) // OP-33278: a row without any name is not selectable, drop it
.filter(doctor => doctor.label !== '') : [];
/**
 * Maps cert doctors with PECOS flag for non-physician missing params flow.
 * Includes pecoshospiceflag so Option component can show PECOS checkmark.
 * Handles API response with camelCase or snake_case.
 */

export const mapCertDoctorsWithPecos = doctorArray => (doctorArray !== null && doctorArray !== void 0 ? doctorArray : []).map(doctor => {
  var _doctor$firstName, _doctor$lastName, _doctor$credentials, _ref, _doctor$id;

  const firstName = (_doctor$firstName = doctor.firstName) !== null && _doctor$firstName !== void 0 ? _doctor$firstName : doctor.firstname;
  const lastName = (_doctor$lastName = doctor.lastName) !== null && _doctor$lastName !== void 0 ? _doctor$lastName : doctor.lastname;
  const credentials = (_doctor$credentials = doctor.credentials) !== null && _doctor$credentials !== void 0 ? _doctor$credentials : '';
  const value = (_ref = (_doctor$id = doctor.id) !== null && _doctor$id !== void 0 ? _doctor$id : doctor.personNumber) !== null && _ref !== void 0 ? _ref : doctor.personnumber;
  return _objectSpread(_objectSpread({}, physicianOptionNames(firstName, lastName, credentials)), {}, {
    value,
    npi: (doctor === null || doctor === void 0 ? void 0 : doctor.npi) || '',
    pecoshospiceflag: (doctor === null || doctor === void 0 ? void 0 : doctor.pecoshospiceflag) === 'Y'
  });
}).filter(doctor => String(doctor.label).trim() !== '');
/**
 * API call in any widget
 */

export const getHeaders = async keycloak => {
  if (keycloak && keycloak.token && keycloak.updateToken) {
    const tokenExpirytime = parseInt(process.env.REACT_APP_TOKEN_VALIDITY_IN_SECONDS);
    await keycloak.updateToken(tokenExpirytime).success(refreshed => {
      if (refreshed) {// Token was successfully refreshed
      } // Token is still valid


      const headers = new Headers();
      return headers;
    }).error(() => {
      // Failed to refresh the token, or the session has expired
      keycloak.logout();
    });
  }

  return new Headers();
};
/**
 * @strategy STRATEGY FOR PN NOTES PRINTING BY WIDGET
 * - Care Plan Problem - one time API call and save to CPP Global Context upon loading the app
 *                     - additional API calls upon adding notes to update the CPP Global Context
 *                     - can print from widget and smart chart
 *
 * - Care Plan Frequency - one time API call and save to Local Context upon printing
 *                       - can print from smart chart
 *
 * - Medications         - one time API call and save to Local Context upon printing
 *                       - can print from smart chart
 *
 * - Patient Assistance  - one time API call and save to Local Context upon printing
 *                       - can print from widget and smart chart
 *
 * - Manifest            - multiple API calls by entitytype and entitykey
 *                       - can print from widget and smart chart
 *
 * const { countValues } = useContext(PNCounterContext);
 * countValues = {MFST : [], MM: [], PCPF: [], PCPPv2: [], PCPPT: []}
 *
 * FOR MANIFEST : Triggers multiple API calls by entitytype and entitykey to get the Notes for each form as you click on the row
 * For MEDS, CPP, CPF : Triggers one API calls by entitytype to get the Notes
 *
 * observation/patientprogressnotes/patiententitytype?patientnumber=6525&agency=demo1&entitytype=PCPPv2&entitykey=248
 */

export const notesIncluded = printConfig => {
  const mode = printConfig === null || printConfig === void 0 ? void 0 : printConfig.notesMode;
  return mode === 'all' || mode === 'dateRange';
};
export const notesUseDateRange = printConfig => (printConfig === null || printConfig === void 0 ? void 0 : printConfig.notesMode) === 'dateRange';
export const isIdgPrintFilter = printConfig => (printConfig === null || printConfig === void 0 ? void 0 : printConfig.filter) === 'idgOnly';
export const showPrintNotes = printConfig => notesIncluded(printConfig); // TODO: Simplify by adding 'issandboxform' to dto
// this relies on the name starting with 'REVIEW:'

export const getNonReviewForms = list => list ? list !== null && list !== void 0 && list.length ? list.filter(form => !(form.formname.split(':')[0] === 'REVIEW')) : [] : null;
/**
 * Progress Notes and Observation
 */

const progressNotesByEntityType = progressNoteEntity => progressNoteEntity.reduce((seed, note) => {
  if (note) {
    if (note.entitytypeshortname && seed[note.entitytypeshortname]) {
      const type = note.entitytypeshortname;
      seed[type].push(note);
    } else {
      const type = note.entitytypeshortname;
      seed[type] = [note];
    }
  }

  return seed;
}, {});
/**
 * Progress Notes and Observation
 */


export const patientProgressNoteByEntityType = progressNotes => progressNotes.map(note => {
  if (note.patientprogressnoteentitydto) {
    return _objectSpread(_objectSpread({}, note), {}, {
      patientprogressnoteentitydto: progressNotesByEntityType(note.patientprogressnoteentitydto)
    });
  }

  return note;
});
/**
 * Progress Notes and Observation
 */

export const singlePatientProgressNoteByEntityType = progressNote => {
  if (progressNote.patientprogressnoteentitydto) {
    return _objectSpread(_objectSpread({}, progressNote), {}, {
      patientprogressnoteentitydto: progressNotesByEntityType(progressNote.patientprogressnoteentitydto)
    });
  }

  return progressNote;
};
/**
 * Returns true if the progress note is an IDG note (has at least one linked entity
 * with entitytypeshortname === 'IDG' in patientprogressnoteentitydto).
 * Accepts both raw progressNote (patientprogressnoteentitydto is an array) and
 * progressNoteWithEntityByType (patientprogressnoteentitydto keyed by entity type,
 * as returned by getProgressNotes / getProgressNotesByEntityDTO).
 */

export const isIdgNote = note => {
  const entities = note.patientprogressnoteentitydto;

  if (entities) {
    if (Array.isArray(entities)) {
      return entities.some(e => e && e.entitytypeshortname === 'IDG');
    }

    const idgEntities = entities.IDG;

    if (idgEntities) {
      return Array.isArray(idgEntities) && idgEntities.length > 0;
    }
  }

  return false;
};
/**
 * agency enabled based on global config
 */

const isEmptyString = str => !(str !== null && str !== void 0 && str.length);

export const isAgencyEnabled = (enabledBins, currentUser) => {
  let enabledBinsAry = [];

  if (enabledBins) {
    enabledBinsAry = enabledBins.split(',');
  }

  return Array.isArray(enabledBinsAry) && (enabledBinsAry.includes('*') || currentUser.currentHospiceBin && enabledBinsAry.includes(currentUser.currentHospiceBin));
};
/**
 * override agency based on global config
 */

export const getOverrideAgency = (currentUser, overrideBins) => {
  const isOverrideAgency = isAgencyEnabled(overrideBins, currentUser);
  return isOverrideAgency || false;
}; // cert-app

export const DOCTOR_ROLE_LIST = ['MDR', 'MD', 'NP', 'DO', 'PA'];
/**
 * role groups
 */

export const isAgencyUserDoctorRoleGroup = (currentUser, medNPOrderDoctorBins, medPAOrderDoctorBins) => {
  const isNPEnabled = medNPOrderDoctorBins && !isEmptyString(medNPOrderDoctorBins) && isAgencyEnabled(medNPOrderDoctorBins, currentUser);
  const isPAEnabled = medPAOrderDoctorBins && !isEmptyString(medPAOrderDoctorBins) && isAgencyEnabled(medPAOrderDoctorBins, currentUser);

  if (isNPEnabled || isPAEnabled) {
    return isPhysicianRoleGroup(currentUser) || isNPEnabled && isNPRoleGroup(currentUser) || isPAEnabled && isPARoleGroup(currentUser);
  }

  return isPhysicianRoleGroup(currentUser);
};
export const isUserDoctorRoleGroup = currentUser => currentUser === null || currentUser === void 0 ? void 0 : currentUser.userRoleGroups.includes(Doctor);
export const isUserNurseAideRoleGroup = currentUser => !!currentUser.userRoleGroups.find(roleGroup => roleGroup === Nurse || roleGroup === Aide);
export const isUserDoctorNurseRoleGroup = currentUser => !!currentUser.userRoleGroups.find(roleGroup => roleGroup === Nurse || roleGroup === Doctor);
const PHYSICIAN_ROLE_LIST = ['MDR', 'MD', 'DO', 'HP']; // Volunteer Coordinator

const isVCRoleGroup = currentUser => currentUser === null || currentUser === void 0 ? void 0 : currentUser.userRoles.includes(VolunteerCoordinatorRG);

const isNPRoleGroup = currentUser => currentUser === null || currentUser === void 0 ? void 0 : currentUser.userRoles.includes('NP');

const isPARoleGroup = currentUser => currentUser === null || currentUser === void 0 ? void 0 : currentUser.userRoles.includes('PA');

const isPhysicianRoleGroup = currentUser => !!currentUser.userRoles.some(x => PHYSICIAN_ROLE_LIST.includes(x)); // Users in the Aide or Homemaker role groups are forbidden from creating/editing CPP;


const isCPPEditingAllowedRoleGroup = currentUser => !!currentUser.userRoleGroups.some(x => cppEditingAllowedRoleGroup.includes(x));

export const canEditCPPGroup = currentUser => isVCRoleGroup(currentUser) || isCPPEditingAllowedRoleGroup(currentUser);
export const isVolunteerCoRoleGroup = currentUser => !!currentUser.userRoleGroups.find(roleGroup => roleGroup === Nurse || roleGroup === Doctor);
/**
 * Forms and Care Plan Problems Engine
 * 1. double quote cause JSON parsing issue => replace by single quote
 * 2. breakLine is used to break the line instead of //n
 */

const breakLine = 'breakLine';
const SPACE = 'SPACE';
export const getInputEngine = input => {
  const clearedInput = input.replaceAll(/"/g, "'").replaceAll(/{/g, '').replaceAll(/}/g, '').replaceAll(/\[/g, '').replaceAll(/\]/g, '').replaceAll(/\n/g, breakLine);
  return clearedInput;
};
/**
 * Forms and Care Plan Problems Engine
 */

export const getTextEngine = input => {
  const arr = input.split(breakLine);

  if (arr.length) {
    const spaceArr = arr.map(x => x.split(SPACE));
    const countArr = spaceArr.reduce((seed, x) => {
      const num = x[0].length;
      seed.push(num);
      return seed;
    }, []);
    const maxLength = Math.max(...countArr) + 3;
    const resetArr = spaceArr.map(x => {
      const el = x[0];
      const el2 = x[1];

      if (el.length < maxLength) {
        const toFillNum = maxLength - el.length;
        const blank = ' '.repeat(toFillNum);
        let newEl = `${el}${blank}`;

        if (el2) {
          newEl = `${el}${blank}${el2}`;
        }

        return newEl;
      }

      return `${el}${el2}`;
    });
    const stringArr = resetArr.map(el => `${el}${breakLine}`);
    const text1 = stringArr.toString();
    const text2 = text1.replaceAll(`${breakLine},`, breakLine);
    const clearedText = text2.replaceAll(breakLine, '\n');
    return clearedText;
  }

  const clearedText = input.replaceAll(breakLine, '\n');
  return clearedText;
};
export const getCPPTextEngine = input => {
  const clearedText = input.replaceAll(breakLine, '\n');
  return clearedText;
};
/**
 * formatAdminTasks to get the patient info
 */
// TYPE any because Each member of the union type has signatures, but none of those signatures are compatible with each other

export const formatAdminTasks = admintasks => admintasks.length ? admintasks.map(admin => admin.cpptasks.map(task => {
  const {
    patientnumber,
    patient_firstname,
    patient_lastname
  } = admin;
  return _objectSpread(_objectSpread({}, task), {}, {
    patientnumber,
    patient_firstname,
    patient_lastname
  });
})).flat() : [];
export const checkTrue = check => check === 'true' || check === true;
export const isBinInMedOrdersBinList = bin => {
  var _window$env;

  const displayNoteOrderTypes = (_window$env = window.env) === null || _window$env === void 0 ? void 0 : _window$env.displayNoteOrderTypes;
  const medOrderBins = displayNoteOrderTypes === null || displayNoteOrderTypes === void 0 ? void 0 : displayNoteOrderTypes.MedOrderBins;
  const medOrderBinsAry = medOrderBins ? medOrderBins.split(',') : [];
  return medOrderBinsAry.includes(bin);
}; // for DirectBillable|DirectNonBillable

export const defaultBillable = string => string !== null && string !== void 0 && string.includes('|') ? string === null || string === void 0 ? void 0 : string.split('|')[0] : string; // Determines if IDGDocument is open or not

export const isIDGDocumentOpen = widgets => widgets && (widgets === null || widgets === void 0 ? void 0 : widgets.find(comp => (comp === null || comp === void 0 ? void 0 : comp.componentName) === IDGDocument)); // Progress Notes & ReactPDF

export const formatMMDDYYhh = n => {
  if (n && n.noteddatetime && n.sitetimezone && n.id !== -1) {
    return moment.utc(n.visitdatetime || n.noteddatetime).tz(n.sitetimezone).format('MM-DD-YY hh:mm a');
  }

  return '';
}; // Patient Orders Widget & ReactPDF

export const getRoles = data => data && Object.entries(data).reduce((seed, val) => {
  if (val[1]) {
    seed.push(val[0]);
  }

  return seed;
}, []);
export const getPrnRoles = pso => ({
  [Nurse]: checkTrue(pso.patientstandingorderprnnurse),
  [SocialWorker]: checkTrue(pso.patientstandingorderprnsocialworker),
  [Spiritual]: checkTrue(pso.patientstandingorderprnspiritual),
  [Aide]: checkTrue(pso.patientstandingorderprnaide),
  [Volunteer]: checkTrue(pso.patientstandingorderprnvol)
});
export const getPsoRoles = pso => ({
  [Nurse]: checkTrue(pso.prnnurse),
  [SocialWorker]: checkTrue(pso.prnsocialworker),
  [Spiritual]: checkTrue(pso.prnspiritual),
  [Aide]: checkTrue(pso.prnaide),
  [Volunteer]: checkTrue(pso.prnvol)
});
const createdBy = en.common.createdBy.defaultMessage;
const physician = en.common.physician.defaultMessage;
const on = en.common.on.defaultMessage;
const have = en.common.have.defaultMessage;
const has = en.common.has.defaultMessage;
export const getCreatedByString = order => `${createdBy} ${order.createpersonfirstname} ${order.createpersonlastname} ${on} ${formatDateMMDDYY(order.createdatetimeshifted)}`;
export const getPhysicianString = order => `${physician}: ${order.physicianfirstname} ${order.physicianlastname} ${order.physiciancredentials}`;
export const getRolesString = (rolesArray, rolesString) => `${rolesString} ${rolesArray.length > 1 ? have : has} ${en.common.prnPerm.defaultMessage}`;
export const isHomeFacility = id => id === -1;
export const getStandingOrderCode = patient => {
  const standingOrderCode = patient.currentFacilityId && isHomeFacility(patient.currentFacilityId) ? PSOHome : PSOFacility;
  return standingOrderCode;
};
/**
 * Date related functionality
 */

export const getStartDate = date => moment(date).startOf('day');
export const isSameDate = (dateA, dateB) => moment(dateA).isSame(dateB, 'day');
export const isSameTime = (dateA, dateB) => moment(dateA).isSame(dateB, 'minute');
/**
 * Tagged Note should display user and time
 */

const getNoteCredential = note => {
  const {
    modifiedpersonname,
    modifiedcredentials,
    credentials,
    personname
  } = note;

  if (modifiedpersonname) {
    if (modifiedcredentials && modifiedcredentials !== 'null') {
      return `${modifiedcredentials} ${modifiedpersonname}`;
    }

    return `${modifiedpersonname}`;
  } else if (personname) {
    if (credentials && credentials !== 'null') {
      return `${credentials} ${personname}`;
    }

    return `${personname}`;
  }

  return ``;
};

export const getTaggedNoteText = note => {
  // todo: replace with noteddatetimeshifted field
  const locDate = moment.utc(note.noteddatetime).tz(note.sitetimezone);
  const date = formatDateTimeMMDDpm(locDate);
  const personCredentials = getNoteCredential(note);
  return `${note.note} - Added by ${personCredentials} on ${date}`;
};
/**
 * Orders Log addendum line (appended below existing order text / signature).
 * Mirrors getTaggedNoteText attribution: "{text} - Added by {creds} on {date}".
 */

export const formatOrderAddendumLine = (additionalText, user) => {
  const siteTimezone = user.siteTimezone || moment.tz.guess();
  const locDate = moment.utc(new Date()).tz(siteTimezone);
  const date = formatDateTimeMMDDpm(locDate);
  const {
    firstName,
    lastName,
    userCredentials
  } = user;
  const personCredentials = userCredentials && userCredentials !== 'null' ? `${userCredentials} ${firstName} ${lastName}` : `${firstName} ${lastName}`;
  return `${additionalText} - Added by ${personCredentials} on ${date}`;
};
export const preventFutureTimesMoment = time => time.isBefore(moment()); // prevents picking times in the future for datepicker

export const preventFutureTimes = time => {
  const currentDate = new Date();
  const selectedDate = new Date(time);
  return currentDate.getTime() > selectedDate.getTime();
};
/**
 * get patient attending physician
 */

export const NoAttendingPhys = 'No Attending  Physician';
export const getAttendingPhysician = patient => {
  var _patient$patientPhysi;

  const name = patient.patientPhysicianDTOList && ((_patient$patientPhysi = patient.patientPhysicianDTOList.find(currentPhysician => currentPhysician.physicianType === ATTENDING_PHYSICIAN)) === null || _patient$patientPhysi === void 0 ? void 0 : _patient$patientPhysi.physicianName);
  const physicianName = name && name !== NoAttendingPhys ? name : undefined;
  return physicianName;
}; // If Patient has an MD Attending Physician i.e not a PA or NP

export const getAttendingPhysicianDTO = patient => {
  const attendingPhys = patient.patientPhysicianDTOList && patient.patientPhysicianDTOList.find(currentPhysician => currentPhysician.physicianType === ATTENDING_PHYSICIAN);

  if (attendingPhys && attendingPhys.roleDtos && attendingPhys.roleDtos.length) {
    const PAorNP = attendingPhys.roleDtos.filter(x => x.shortName === 'PA' || x.shortName === 'NP');

    if (!PAorNP.length) {
      return attendingPhys;
    }
  }

  return undefined;
};
export const hasTimerNoDuration = x => !x || x && (x[0] === '00' || x[0] === '0' || x[0] === 0) && (x[1] === '00' || x[1] === '0' || x[1] === 0);
export const hasTimerDuration = x => !hasTimerNoDuration(x); // suggest box

export const getSuggestBoxOptions = values => {
  const options = values.length ? values.map(tasktext => ({
    label: tasktext.toUpperCase(),
    value: tasktext
  })) : [];
  return options;
};
export const allMeasuresNameMapped = measures => measures.reduce((seed, val) => {
  if (val.shortname) {
    var _ref2, _val$description;

    // Null-prototype dict: shortname "map" must not collide with Object/Array .map.
    seed[val.shortname] = (_ref2 = (_val$description = val.description) !== null && _val$description !== void 0 ? _val$description : val.name) !== null && _ref2 !== void 0 ? _ref2 : '';
  }

  return seed;
}, Object.create(null));
export const getSiteId = () => {
  const params = new URLSearchParams(window.location.search);
  const siteId = params.get('siteId');
  return siteId || params.get('siteid');
};
/**
 * VISIT HOPE
 * If the current visit has a visit attribute of HAD, then you cannot turn of required questions for the INA (Initial Nursing Assessment);
 * if the current visit has a visit attribute including HUV1 or HUV2, then you cannot turn off required questions for the NVR (Nursing Visit Record)
 */

export const checkINAAttributes = visit => {
  const attributesHAD = visit.visitAttributes.find(x => x.shortname === HAD);
  return isVisitAttributeSelected(attributesHAD);
};
export const checkHUVAttributes = visit => {
  const attributesHUV = visit.visitAttributes.find(x => x.shortname === HUV1 && isVisitAttributeSelected(x) || x.shortname === HUV2 && isVisitAttributeSelected(x));
  return !!attributesHUV;
}; // if positive CANNOT turn off required question

export const checkVisitAttribute = (form, visit) => {
  let res = false;
  const name = form.name || form.displayname || form.formname;

  if (name.includes(INAForm)) {
    res = checkINAAttributes(visit);
  } else if (name.includes(NVRForm)) {
    res = checkHUVAttributes(visit);
  }

  return res;
}; // Questionnaires

export const sortDateAsc = data => data && data.length ? data.sort((a, b) => {
  const aStart = a.createdatetime || false;
  const bStart = b.createdatetime || false;
  return new Date(aStart) < new Date(bStart) ? 1 : -1;
}) : data; // is array only empty string

export const isEmptyArray = arr => {
  if (!arr || !arr.length) {
    return true;
  }

  if (Array.isArray(arr) && arr.length) {
    const isEmpty = arr.every(x => x.toString().trim() === '');
    return isEmpty;
  }

  return false;
};
export const getUserName = user => `${user.firstName} ${user.lastName}${user.userCredentials && `, ${user.userCredentials}` || ''}`;
export const getUserCredentials = user => {
  const {
    firstName,
    lastName,
    userCredentials
  } = user;
  const userTitle = userCredentials ? `${firstName} ${lastName}, ${userCredentials}` : `${firstName} ${lastName}`;
  return userTitle;
};
export const getLocalOrderTime = user => {
  const tz = moment.tz.guess();
  const siteTimezone = user ? user.siteTimezone : tz;
  const tzDate = moment.utc(new Date()).tz(siteTimezone).format('MM/DD/YYYY HH:mm:ss');
  return tzDate;
};
/**
 * Returns the display name for a patient manifest form, appending the
 * supplementaltitle in parentheses when present (e.g. "IDG v3 (Initial Plan of Care)").
 */

export const getFormDisplayName = form => {
  if (form.supplementaltitle) {
    return `${form.displayname} (${form.supplementaltitle})`;
  }

  return form.displayname;
};
/**
 * Chrome 153+ and Safari no longer stroke zero-length canvas subpaths, which is
 * exactly what react-canvas-draw builds for a click that never moves. Fill a dot
 * for those strokes; anything with real movement keeps the library's own path.
 */

export const applyPainMapDotFix = canvasDraw => {
  if (!canvasDraw || canvasDraw.painMapDotFixApplied) {
    return;
  }

  const drawStroke = canvasDraw.drawPoints;
  canvasDraw.painMapDotFixApplied = true;

  canvasDraw.drawPoints = args => {
    const {
      points,
      brushColor,
      brushRadius
    } = args;
    const [origin] = points || [];
    const isDot = origin && points.every(p => p.x === origin.x && p.y === origin.y);

    if (!isDot) {
      drawStroke(args);
      return;
    }

    const ctx = canvasDraw.ctx.temp;
    canvasDraw.clearWindow(ctx);
    ctx.fillStyle = brushColor;
    ctx.beginPath();
    ctx.arc(origin.x, origin.y, brushRadius, 0, Math.PI * 2);
    ctx.fill();
  };
};
export * from './primitives';